import type {ApiWorkforceProfileRecord} from '../WorkforceProfiles/workforceProfileApiTypes';

import {
  mapInferredCulturalRationale,
  mapRequiredLanguageProfile,
} from './mapRequiredLanguageProfile';
import {ApiSerializer} from '../../../../../serializers/ApiSerializer';
import type {
  WorkforceProfileInferredCulturalAnswer,
  WorkforceProfileInferredCulturalMethodItem,
  WorkforceProfileInferredCulturalScaleConfiguration,
} from '../../types/WorkforceProfileTypes';


export const mapScaleConfiguration = (
  scale: ApiWorkforceProfileRecord | undefined
): WorkforceProfileInferredCulturalScaleConfiguration | null => {
  if (!scale) {
    return null;
  }

  const labelLeft = mapRequiredLanguageProfile(scale.labelLeft);
  const labelRight = mapRequiredLanguageProfile(scale.labelRight);

  if (!labelLeft || !labelRight) {
    return null;
  }

  const minValue = Number(scale.minValue ?? 1);
  const maxValue = Number(scale.maxValue ?? 6);
  const step = Number(scale.step ?? 1);

  if (Number.isNaN(minValue) || Number.isNaN(maxValue) || Number.isNaN(step)) {
    return null;
  }

  return {minValue, maxValue, step, labelLeft, labelRight};
};

export const mapInferredCulturalMethodItem = (
  raw: ApiWorkforceProfileRecord | undefined
): WorkforceProfileInferredCulturalMethodItem | null => {
  if (!raw) {
    return null;
  }

  const title = mapRequiredLanguageProfile(raw.title);
  const scaleConfiguration = mapScaleConfiguration(raw.scaleConfiguration as ApiWorkforceProfileRecord | undefined);

  if (!title || !scaleConfiguration) {
    return null;
  }

  return {
    resourceId: String(raw.resourceId ?? ''),
    humanReadableIdentifier: String(raw.humanReadableIdentifier ?? ''),
    title,
    scaleConfiguration,
  };
};

const mapInferredCulturalAnswerEntry = (entry: unknown): Omit<WorkforceProfileInferredCulturalAnswer, 'key'> | null => {
  const normalized = (
    entry && typeof entry === 'object' ? ApiSerializer.toCamelCase(entry) : entry
  ) as ApiWorkforceProfileRecord;

  const value = Number(normalized.value);

  if (Number.isNaN(value)) {
    return null;
  }

  return {
    value,
    rationale: mapInferredCulturalRationale(normalized),
    methodItem: mapInferredCulturalMethodItem(normalized.methodItem as ApiWorkforceProfileRecord | undefined),
  };
};

export const mapInferredCulturalAnswers = (raw: unknown): WorkforceProfileInferredCulturalAnswer[] => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return [];
  }

  return Object.entries(raw as Record<string, unknown>)
    .map(([key, entry]) => {
      const mapped = mapInferredCulturalAnswerEntry(entry);

      if (!mapped) {
        return null;
      }

      return {key, ...mapped};
    })
    .filter((answer): answer is WorkforceProfileInferredCulturalAnswer => answer != null)
    .sort((a, b) => a.key.localeCompare(b.key));
};
